import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import { useGlobalState } from "../../context/useGlobalState";
import { getProjectByTypes, updateNewsletterItemFromProject } from "../../api";
import TextInput from "../../components/ui/TextInput";
import Dropdown from "../../components/common/Dropdown";
import { NewsletterAI } from "../../types/types";
import backIcon from "../../assets/images/arrow-left-fill.svg";
import e from "./Edition.module.css";

interface FieldErrors {
    selectedContentLanguage: boolean;
    singleTitle: boolean;
}

const ModifyItemToNewsletterProject: React.FC<RefreshProjectProps> = () => {
    const { id, itemId } = useParams<{ id: string; itemId: string }>();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();
    const [newsletterItem, setNewsletterItem] = useState<NewsletterAI | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [singleTitle, setSingleTitle] = useState<string>("");
    const [singleText, setSingleText] = useState<string>("");

    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        selectedContentLanguage: false,
        singleTitle: false,
    });
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);

    const token = localStorage.getItem("token");

    const fetchNewsletterItem = async () => {
        if (!token || !id || !itemId) return;
        try {
            const response = await getProjectByTypes({ token, projectId: id, targetTypes: ["newsletter"] });
            const item = response.project.newsletterAI?.find((item: NewsletterAI) => item.id === itemId);
            if (item) {
                setNewsletterItem(item);
                setSingleTitle(item.name);
                setSingleText(item.text || "");
                const findLanguage = data.languagesAI?.find((lang) => lang.code === item.language);
                setSelectedContentLanguage(findLanguage?.value || "");
            }
        } catch (error) {
            console.error("Error fetching newsletter item:", error);
        }
    };

    useEffect(() => {
        if (itemId && data.languagesAI && data.languagesAI.length > 0) {
            fetchNewsletterItem();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId, data.languagesAI]);

    useEffect(() => {
        const errors: FieldErrors = {
            selectedContentLanguage: !selectedContentLanguage,
            singleTitle: !singleTitle.trim(),
        };
        setFieldErrors(errors);
        setIsGenerateButtonDisabled(Object.values(errors).some((err) => err));
    }, [selectedContentLanguage, singleTitle, singleText]);

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => setShowErrors(false), 3000);
    };

    const handleSave = async () => {
        setIsLoading(true);
        if (!singleTitle.trim() && !singleText.trim() && !selectedContentLanguage) {
            setIsLoading(false);
            showErrorMessage();
            return;
        }
        try {
            if (!token || !id || !itemId) {
                setIsLoading(false);
                return;
            }
            const languageCode = data.languagesAI?.find((lang) => lang.value === selectedContentLanguage)?.code;
            if (!languageCode) {
                setIsLoading(false);
                return;
            }
            const resp = await updateNewsletterItemFromProject({
                token,
                projectId: id,
                itemId,
                language: languageCode,
                title: singleTitle,
                text: singleText,
            });
            if (resp && resp.message) navigate(`/newsletter-projects/${id}`);
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to save newsletter item:", error);
            setIsLoading(false);
        }
    };

    if (!newsletterItem) {
        return <div className={e.page}>{t("loading")}...</div>;
    }

    const langCode = data.languagesAI?.find((lang) => lang.value === selectedContentLanguage)?.code || "";
    const wordCount = singleText.trim() ? singleText.trim().split(/\s+/).length : 0;
    const charCount = singleText.length;

    return (
        <div className={e.page}>
            <button className={e.breadcrumb} onClick={() => navigate(`/newsletter-projects/${id}`)}>
                <img src={backIcon} alt="" style={{ width: 12, height: 12 }} />
                {t("allProjects")}
            </button>

            <div className={e.header}>
                <div className={e.titleRow}>
                    <h1 className={e.title}>{singleTitle || t("whatIsTheTitleOfYourNewsletter")}</h1>
                    {langCode && <span className={e.badge}>{langCode.toUpperCase()}</span>}
                </div>
                <div className={e.headerActions}>
                    <button
                        className={e.primaryBtn}
                        onClick={handleSave}
                        disabled={isGenerateButtonDisabled || isLoading}
                    >
                        {isLoading ? t("processing") : t("save")}
                    </button>
                </div>
            </div>

            <div className={e.metaCard} style={{ gridTemplateColumns: "1fr" }}>
                <div className={e.field}>
                    <p className={e.fieldLabel}>{t("language")}</p>
                    <Dropdown
                        label=""
                        options={data.languagesAI}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                        isMissing={showErrors && fieldErrors.selectedContentLanguage}
                    />
                </div>
            </div>

            <div className={e.panel}>
                <div className={e.titleField}>
                    <p className={e.fieldLabel}>{t("whatIsTheTitleOfYourNewsletter")}</p>
                    <TextInput
                        placeholder={t("pleaseInsertYourText")}
                        value={singleTitle}
                        onChange={(ev) => setSingleTitle(ev.target.value)}
                        size={"medium"}
                        isMissing={showErrors}
                    />
                </div>
                <div className={e.panelHeader}>
                    <div>
                        <h3 className={e.panelTitle}>{t("textEdition")}</h3>
                        <p className={e.panelSubtitle}>{t("textEditionSubNewsletter")}</p>
                    </div>
                </div>
                <textarea
                    className={e.textarea}
                    placeholder={t("pleaseInsertYourText")}
                    value={singleText}
                    onChange={(ev) => setSingleText(ev.target.value)}
                />
                <div className={e.panelFooter}>
                    <span>
                        {wordCount} {t("wordsLabel")} · {charCount} {t("charactersLabel")}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ModifyItemToNewsletterProject;
